import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
  GET_ALL_ROLES,
  Role,
  RoleSelectionViewModel,
} from '../../../apollo/queries/getAllRoles.queries';

export interface Category {
  categoryGroupName: string;
  categoryName: string;
  roles: Role[];
}

export interface SearchPair {
  key: string;
  value: string;
}

export const defaultSelectedCategory: Category = {
  categoryGroupName: '',
  categoryName: '',
  roles: [],
};

type RolesByOrg = {
  [key: string]: RoleSelectionViewModel[];
};

export const useRoles = () => {
  const { code } = useParams<{ code: string }>();
  const [rolesByOrg, setRolesByOrg] = useState<RolesByOrg>({});
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    defaultSelectedCategory,
  );

  const getRolesSelectionByOrg = (orgCode: string) => {
    const roles = rolesByOrg[orgCode] || [];
    return roles.slice();
  };

  const isPresentForOrg = (code: string) => {
    return getRolesSelectionByOrg(code).length > 0;
  };

  const setDefaultSelectedCategory = (rolesModel: RoleSelectionViewModel[]) => {
    const [roleSelection] = rolesModel;

    setSelectedCategory({
      categoryGroupName: roleSelection.categoryGroupName,
      ...roleSelection.categories[0],
    });
  };

  const [fetchRoles] = useLazyQuery(GET_ALL_ROLES, {
    fetchPolicy: 'network-only',
  });

  const fetchRolesSelection = async (code: string) => {
    if (code !== '' && !isPresentForOrg(code)) {
      try {
        const { data } = await fetchRoles({
          variables: { organisation: code },
        });

        const rolesModel = data.allRolesSelectionViewModel;
        if (rolesModel) {
          setRolesByOrg((prev) => ({
            ...prev,
            [code]: rolesModel,
          }));

          if (selectedCategory.categoryName === '') {
            setDefaultSelectedCategory(rolesModel);
          }
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  useEffect(() => {
    fetchRolesSelection(code || '');
  }, []);

  return {
    onCategorySelect: setSelectedCategory,
    getRolesSelectionByOrg,
    fetchRolesSelection,
    selectedCategory,
  };
};
